<template>
    <b-field :label="help ? undefined : label" :horizontal="horizontal" :type="fieldStatusType">
        <template v-if="help" #label>
            <field-label :field="{ label, help }" />
        </template>
        <BAutocomplete
            ref="bAutocomplete"
            v-model="query"
            :data="results"
            :loading="loading"
            :placeholder="placeholder || $t('Search employee...')"
            field="name"
            :open-on-focus="true"
            :icon="icon"
            :size="size"
            :expanded="expanded"
            :disabled="disabled"
            clearable
            append-to-body
            @input="onInput"
            @select="onSelect"
        >
            <template #empty>{{ $t('No results found') }}</template>
        </BAutocomplete>
    </b-field>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, onUnmounted } from 'vue';
import { BAutocomplete } from 'buefy';
import { useOrgStore } from '@/stores/org';
import { useFieldStatus } from '@/composables/useFieldStatus';
import debounce from 'lodash/debounce';

const props = defineProps({
    modelValue: { type: [Number, String], default: null },
    label: { type: String, default: '' },
    help: { type: String, default: null },
    horizontal: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    expanded: { type: Boolean, default: true },
    exclude: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    icon: { type: String, default: 'magnify' },
    size: { type: String, default: '' },
    sourceUrl: { type: String, default: '/employees' },
    // Restrict suggestions to members holding an ability on this section, so we
    // never propose someone who could not open the record anyway.
    ability: { type: String, default: null }
});

// `select` carries the whole row alongside the id, for the callers that need
// more than the reference — an employment file copies the name across so the
// person stays nameable if the account is later cleared.
const emit = defineEmits(['update:modelValue', 'select']);

const orgStore = useOrgStore();

// This component's own inner b-field shadows the parent field, so Buefy never
// copies the parent's is-danger status here. Forward it explicitly so the control
// gets the red border like plain fields when the parent field is in error.
const fieldStatusType = useFieldStatus();

const query = ref('');
const results = ref([]);
const loading = ref(false);
const bAutocomplete = ref(null);
let resolving = false;

function onDocMousedown(e) {
    const ac = bAutocomplete.value;
    if (!ac) return;
    const outside = !ac.$el?.contains(e.target) && !ac.$refs?.dropdown?.contains(e.target);
    if (outside) ac.isActive = false;
}

onMounted(() => {
    fetchEmployees();
    document.addEventListener('mousedown', onDocMousedown);
});

onBeforeUnmount(() => {
    const bodyEl = bAutocomplete.value?.$data?._bodyEl;
    if (bodyEl?.parentNode) {
        bodyEl.parentNode.removeChild(bodyEl);
        bAutocomplete.value.$data._bodyEl = null;
    }
});

onUnmounted(() => {
    document.removeEventListener('mousedown', onDocMousedown);
});

function onInput() {
    if (resolving) return;
    search();
}

const search = debounce(function () {
    fetchEmployees();
}, 300);

async function fetchEmployees() {
    // The opening list — no search term, nothing to pin — is the org roster every
    // other picker on the page also wants, so take it from the store's cached copy
    // instead of firing one identical GET per mounted autocomplete.
    if (!query.value && !props.modelValue && props.sourceUrl === '/employees') {
        loading.value = true;
        const list = await orgStore.loadEmployees();
        results.value = list.filter(e => !props.exclude.includes(e.id));
        loading.value = false;
        return;
    }

    loading.value = true;
    try {
        const queryParam = encodeURIComponent(query.value || '');
        const idParam = props.modelValue ? `&id=${props.modelValue}` : '';
        const abilityParam = props.ability ? `&ability=${encodeURIComponent(props.ability)}` : '';
        const response = await axios.get(
            `${orgStore.api}${props.sourceUrl}?query=${queryParam}${idParam}${abilityParam}`
        );
        const list = response.data.data || response.data;
        results.value = list.filter(e => !props.exclude.includes(e.id));

        if (props.modelValue && !query.value) {
            const selected = list.find(e => e.id == props.modelValue);
            if (selected) {
                resolving = true;
                query.value = selected.name;
                resolving = false;
            }
        }
    } catch (e) {
        // silently fail
    } finally {
        loading.value = false;
    }
}

function onSelect(employee) {
    if (!employee) {
        emit('update:modelValue', null);
        emit('select', null);
        return;
    }
    query.value = employee.name;
    emit('update:modelValue', employee.id);
    emit('select', employee);
}

watch(
    () => props.modelValue,
    (newVal, oldVal) => {
        if (!newVal) {
            query.value = '';

            return;
        }

        if (newVal === oldVal) {
            return;
        }

        // The user just picked from the list, so the name is already at hand —
        // going through the server would blank the field until it answers.
        const known = results.value.find(e => e.id == newVal);
        if (known) {
            resolving = true;
            query.value = known.name;
            resolving = false;

            return;
        }

        // Set from code. Clear first: fetchEmployees() only writes the name into
        // an empty query, so without this the field would keep showing the
        // previous employee.
        query.value = '';
        fetchEmployees();
    }
);
</script>
